<template>
  <div class="audit-panel">
    <div v-if="shipCode && !isShipSyS" class="audit-panel__manager">
      <div class="audit-panel__manager-row">
        <span class="audit-panel__manager-label">当前审批人</span>
        <strong>{{ approvedManagersList || '无' }}</strong>
      </div>
      <div class="audit-panel__manager-row">
        <span class="audit-panel__manager-label">节点角色</span>
        <strong>{{ currentRoleNamesText }}</strong>
      </div>
    </div>

    <section class="audit-panel__section" aria-labelledby="audit-history-title">
      <div class="audit-panel__section-header">
        <div>
          <h3 id="audit-history-title">审批历史</h3>
          <span>{{ hisTask.length }} 条记录</span>
        </div>
        <v-btn
          v-if="!isShip"
          text
          small
          color="primary"
          class="d-print-none"
          :loading="loading"
          @click="loadPic"
        >
          <v-icon left small>mdi-file-tree-outline</v-icon>
          查看流程图
        </v-btn>
      </div>
      <v-audit-timeline :items="hisTask" :status-map="statusMap" />
    </section>

    <section
      v-if="hasPendingAction"
      class="audit-panel__section audit-panel__action"
      aria-labelledby="audit-action-title"
    >
      <div class="audit-panel__section-header">
        <div>
          <h3 id="audit-action-title">审批意见</h3>
          <span>请选择处理结果并填写必要说明</span>
        </div>
      </div>
      <v-radio-group
        ref="radio"
        v-model="adopt"
        :rules="[rules.radio]"
        class="audit-panel__radio-group mt-1"
        row
      >
        <v-radio label="同意" :value="true"></v-radio>
        <v-radio v-if="!onlyYes" label="退回" :value="false"></v-radio>
      </v-radio-group>
      <v-textarea
        ref="textarea"
        v-model="comment"
        label="批注"
        outlined
        dense
        auto-grow
        rows="3"
        :rules="adopt ? [] : [rules.required]"
      ></v-textarea>
    </section>

    <v-dialog v-model="dialog" max-width="1400" scrollable>
      <v-card>
        <v-card-title>
          审批流程图
          <v-spacer></v-spacer>
          <v-btn icon aria-label="关闭流程图" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0 audit-panel__diagram">
          <v-approval-diagram-viewer
            ref="diagramViewer"
            :src="img"
          ></v-approval-diagram-viewer>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'v-audit',
  props: {
    auditParams: {
      type: Object,
      default: () => ({
        taskId: '',
        processInstanceId: '',
      }),
    },
    showForm: {
      type: Boolean,
      default: true,
    },
    shipCode: {
      type: String,
      default: '',
    },
    onlyYes: {
      type: Boolean,
      default: false,
    },
  },
  inject: {
    form: { default: null },
    approvalPanelController: { default: null },
  },
  data() {
    return {
      dialog: false,
      img: null,
      adopt: '',
      hisTask: [],
      comment: '',
      loading: false,
      rules: {
        radio: (v) => v !== '' || '请选择审批意见',
        required: (v) => !!v || '请输入批注',
      },
      approvedManagersList: '',
      currentRoleNames: [],
      isRoleManagerNew: true,
      isShip: false,
      isShipSyS: this.$local.data.get('userInfo').isShipSyS,
      statusMap: {
        success: '审批通过',
        reject: '审批驳回',
      },
    }
  },
  computed: {
    currentRoleNamesText() {
      return this.currentRoleNames.length
        ? this.currentRoleNames.join('、')
        : '无'
    },
    hasPendingAction() {
      return Boolean(
        this.auditParams.taskId &&
          !this.auditParams.isReject &&
          this.isRoleManagerNew,
      )
    },
  },
  watch: {
    hasPendingAction: {
      immediate: true,
      handler(pending) {
        this.approvalPanelController?.setPending(pending)
      },
    },
  },
  created() {
    this.form && this.form.register(this)
    this.approvalPanelController?.register()
    this.isShip = this.$local.data.get('userInfo').isShipSyS
  },
  beforeDestroy() {
    this.form && this.form.unregister(this)
    this.approvalPanelController?.unregister()
  },
  mounted() {
    this.loadHisTask()
    this.loadCurrentRoleNames()
    if (this.shipCode && !this.isShipSyS) {
      this.loadPerson()
    }
  },
  methods: {
    async loadPerson() {
      if (!this.auditParams.processInstanceId) {
        this.approvedManagersList = '无'
        return
      }

      const manager = await this.getAsync(
        '/business/common/ship/managementOwner/pageNew',
        { size: 100, shipCode: this.shipCode },
      )
      const approved = await this.getAsync(
        '/flow/task/getPersonByTaskIdAndShipCode',
        {
          taskId: this.auditParams.processInstanceId,
          shipCode: this.shipCode,
        },
      )
      const approvedNamesRaw = Array.isArray(approved.data) ? approved.data : []
      const approvedNames = approvedNamesRaw
        .map((name) => (typeof name === 'string' ? name.trim() : ''))
        .filter((name) => !!name)
      const approvedSet = new Set(
        approvedNames.map((name) => name.toLowerCase()),
      )
      const uniqueManagers = new Set()
      const managerRecords = Array.isArray(manager?.data?.records)
        ? manager.data.records
        : []

      managerRecords.forEach((record) => {
        const name = record?.managerName?.trim() || ''
        if (name && approvedSet.has(name.toLowerCase())) {
          uniqueManagers.add(name)
        }
      })

      const approvedManagers = Array.from(uniqueManagers)
      this.approvedManagersList = approvedManagers.length
        ? approvedManagers.join(', ')
        : '无'

      const userInfo = this.$local.data.get('userInfo')
      this.isRoleManagerNew =
        this.approvedManagersList.includes(userInfo.nickName) ||
        userInfo.roleName == '管理员'
    },
    async loadHisTask() {
      const { data } = await this.getAsync(
        '/flow/task/getHisTaskInsListByProInsId',
        {
          processInstanceId: this.auditParams.processInstanceId,
        },
      )
      this.hisTask = Array.isArray(data) ? data : []
    },
    async loadCurrentRoleNames() {
      if (!this.auditParams.processInstanceId) {
        this.currentRoleNames = []
        return
      }
      const { data } = await this.getAsync(
        '/flow/task/getCurrentTaskRoleNames',
        { processInstanceId: this.auditParams.processInstanceId },
      )
      this.currentRoleNames = Array.isArray(data)
        ? [...new Set(data.filter((name) => !!name))]
        : []
    },
    arrayBufferToBase64(buffer) {
      let binary = ''
      const bytes = new Uint8Array(buffer)
      const len = bytes.byteLength
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      return `data:image/bmp;base64,${window.btoa(binary)}`
    },
    async loadPic() {
      this.loading = true
      try {
        const result = await this.getArrayBuffer(
          '/flow/diagram/getTracePicByProcessInstanceId',
          { processInstanceId: this.auditParams.processInstanceId },
        )
        this.img = this.arrayBufferToBase64(result.data)
        this.dialog = true
        this.$nextTick(() => this.$refs.diagramViewer?.reset())
      } finally {
        this.loading = false
      }
    },
    focusApprovalField(refName) {
      this.approvalPanelController?.open()
      this.$nextTick(() => {
        const field = this.$refs[refName]
        const input = field?.$el?.querySelector('input, textarea')
        input?.focus()
      })
    },
    validate(force, value) {
      if (!this.$refs.radio) return true
      if (!this.$refs.radio.validate(force, value)) {
        this.focusApprovalField('radio')
        return false
      }
      if (!this.$refs.textarea.validate(force, value)) {
        this.focusApprovalField('textarea')
        return false
      }
      return true
    },
    async submit() {
      if (!this.auditParams.taskId) return null
      if (this.auditParams.isReject) this.adopt = true
      const { errorRaw } = await this.postAsync(
        '/flow/task/completeTaskAndCommentAndSetVar',
        {
          taskId: this.auditParams.taskId,
          comment: this.comment,
          adopt: this.adopt,
          params: {},
        },
      )
      return errorRaw
    },
  },
}
</script>
